import { useLocation } from 'react-router-dom';
import { useTenantAuth } from '../contexts/TenantAuthContext';
import {
  AccessLevel,
  getAccessLevel,
  canAccess as canAccessFn,
  isReadOnlyAccess,
  isDisabledAccess as isDisabledAccessFn,
  hasFullAccess,
  getSettingsSectionAccess,
} from '../lib/permissions';

/**
 * Hook to check permissions for the current route or a specific path.
 *
 * Usage:
 *   const { isReadOnly, canAccess, accessLevel } = usePermissions();          // current route
 *   const { isReadOnly, canAccess, accessLevel } = usePermissions('/keys');   // specific path
 */
export function usePermissions(overridePath?: string) {
  const { admin } = useTenantAuth();
  const location = useLocation();

  const role = admin?.role;
  const path = overridePath ?? location.pathname;

  const accessLevel: AccessLevel = getAccessLevel(role, path);

  return {
    /** The resolved access level: 'full' | 'read-only' | 'disabled' | 'hidden' */
    accessLevel,

    /** true when the route/path is accessible (not hidden) */
    canAccess: canAccessFn(role, path),

    /** true when the route/path is read-only */
    isReadOnly: isReadOnlyAccess(role, path),

    /** true when the route/path has full read-write access */
    hasFullAccess: hasFullAccess(role, path),

    /** Check access for a settings section (e.g., 'aeo', 'contacts', 'widget') */
    getSettingsAccess: (section: string): AccessLevel =>
      getSettingsSectionAccess(role, section),

    /** Check if a settings section is read-only */
    isSettingsReadOnly: (section: string): boolean =>
      getSettingsSectionAccess(role, section) === 'read-only',

    /** Check if a settings section is disabled (visible but not interactive) */
    isSettingsDisabled: (section: string): boolean =>
      getSettingsSectionAccess(role, section) === 'disabled',

    /** Check if a settings section is hidden */
    isSettingsHidden: (section: string): boolean =>
      getSettingsSectionAccess(role, section) === 'hidden',

    /** true when the route/path is disabled */
    isDisabled: isDisabledAccessFn(role, path),

    /** The current user role */
    role,
  };
}
